// Where a ticket actually gets placed: the bookmaker's own page for the bet,
// its front door when there is no such page, and the words said on the way out.
//
// A DEEP LINK IS ONLY EVER A URL A FEED HANDED US. Nothing here builds one, and
// nothing here guesses a scheme: a URL is carried on the price row that quoted
// the leg — PuntersEdge writes `source_url` per book on a runner, The Odds API
// writes `link` on a bookmaker, a market or an outcome when `includeLinks=true`
// — and it rides onto the leg unchanged (`legLink` below). A guessed URL is a
// button that opens a 404, which is worse than no button, because it is a
// button somebody trusted.
//
// AND IT IS CHECKED BEFORE IT IS OPENED (`bookLink`): https, and the host is
// the book's own — the domain `BOOKMAKER_SITES` already names for that key, or
// a subdomain of it. A feed that answers with somebody else's host, or with
// http, gets the front door instead. The app hands these URLs to the OS, which
// opens the INSTALLED app on them; an unchecked one would be this app opening
// whatever an upstream row said to open.
//
// WHAT AUSTRALIAN BOOKS ACTUALLY RETURN is not known here and is not guessed:
// racing links are verified (TAB, PointsBet, Ladbrokes, Neds, Unibet all answer
// a race page), and for the sports board the adapter logs a one-line summary of
// which books carried a link on each fetch — that log is the answer. Where the
// link is an outcome's it is a BETSLIP link, and opening it pre-adds the
// selection; the button says so ("Add to TAB slip") rather than "Open at TAB".
//
// The slip on the clipboard (`slipText`, tickets.js) goes with the tap either
// way: no AU book publishes a URL that builds a multi, so a multi is always
// first leg opened, rest pasted.
//
// EVERY URL IS https, NEVER A CUSTOM SCHEME. Each of these apps claims its own
// domain as a universal link, so handing the https URL to the OS opens the
// INSTALLED app when there is one and the browser when there is not — one URL,
// both cases, nothing to feature-detect. `sportsbet://` would fail silently on
// a phone without the app, and silently is the one way a handoff must not fail.
//
// The domains below were each checked to answer on the open web rather than
// recalled. bet365 answers 403 to anything that is not a browser (Cloudflare in
// front of it); the domain is right and a real WebView gets the site.

// Nothing is imported here, `odds.js` included: that module reaches for storage
// and the provider keys the moment it loads, and this one is a table. The names
// below are held against `BOOKMAKERS` by the test instead, which is where the
// two are allowed to know about each other.

/**
 * Key → the book's name and its Australian site.
 *
 * Keyed by The Odds API's bookmaker key, which is what a leg and a single's
 * `multi.bookmakerKey` both carry, so nothing has to match a display string
 * back to a bookmaker. The test pins this against `BOOKMAKERS` in both
 * directions: a book the board can price and this file cannot open would be a
 * ticket with a dead button on it, and a URL for a book the board never names
 * is a link nobody can reach.
 */
export const BOOKMAKER_SITES = {
  sportsbet: { name: 'Sportsbet', url: 'https://www.sportsbet.com.au/' },
  tab: { name: 'TAB', url: 'https://www.tab.com.au/' },
  ladbrokes_au: { name: 'Ladbrokes', url: 'https://www.ladbrokes.com.au/' },
  neds: { name: 'Neds', url: 'https://www.neds.com.au/' },
  pointsbetau: { name: 'PointsBet', url: 'https://pointsbet.com.au/' },
  bet365_au: { name: 'Bet365', url: 'https://www.bet365.com.au/' },
  unibet: { name: 'Unibet', url: 'https://www.unibet.com.au/' },
  betr_au: { name: 'Betr', url: 'https://www.betr.com.au/' },
  tabtouch: { name: 'TABtouch', url: 'https://www.tabtouch.com.au/' },
  playup: { name: 'PlayUp', url: 'https://www.playup.com.au/' },
  betright: { name: 'Bet Right', url: 'https://www.betright.com.au/' },
  dabble_au: { name: 'Dabble', url: 'https://www.dabble.com.au/' },
  betfair_ex_au: { name: 'Betfair Exchange', url: 'https://www.betfair.com.au/' },
};

/** `{ key, name, url }` for a bookmaker this app can open, or null. */
export function bookmakerSite(key) {
  const k = String(key ?? '');
  const site = BOOKMAKER_SITES[k];
  return site ? { key: k, ...site } : null;
}

// --- the link, and what makes one safe to open --------------------------------

/** A host with the `www.` taken off, lowercased. "WWW.Tab.com.au" → "tab.com.au". */
const baseHost = (host) => String(host ?? '').toLowerCase().replace(/^www\./, '');

/**
 * A URL THIS APP WILL OPEN AT `key`, or null — the whole validation rule.
 *
 * Two things are checked and nothing else is:
 *
 *   https, never http and never a custom scheme. The https URL is what the OS
 *   resolves to the installed app (see the note above `BOOKMAKER_SITES`), and
 *   http would be this app downgrading a bet slip onto a plain socket.
 *
 *   The book's OWN host: the domain already written beside that key, or a
 *   subdomain of it — `tab.com.au`, `www.tab.com.au` and `sportsbook.tab.com.au`
 *   are all TAB, and `sportsbet.com` is not `sportsbet.com.au`. A row is
 *   upstream data, and a URL from a row is a place this app would send a punter
 *   with their slip on the clipboard; one that does not belong to the book the
 *   ticket names is refused rather than followed.
 *
 * A book with no address of its own (the four racing-only shops, an unknown
 * key) can carry no link: there is nothing to hold its host against.
 */
export function bookLink(key, url) {
  const site = BOOKMAKER_SITES[String(key ?? '')];
  if (!site || !url) return null;
  let link;
  let home;
  try {
    link = new URL(String(url));
    home = new URL(site.url);
  } catch {
    return null;
  }
  if (link.protocol !== 'https:') return null;
  const base = baseHost(home.hostname);
  const host = baseHost(link.hostname);
  if (host !== base && !host.endsWith(`.${base}`)) return null;
  return link.href;
}

/**
 * THE LEG'S OWN LINK, off the board row that priced it: `{ link, linkKind }`,
 * or `{}` where the row carried none or the URL did not survive `bookLink`.
 *
 * Spread onto a leg by every printer that builds one (`legFromMatch`,
 * `strategy.js`, `strategies/racing.js`), so the leg carries the URL the book
 * quoting it published, and no leg can carry a link belonging to a different
 * book: the row is looked up BY `bookmakerKey`.
 *
 * `linkKind` is provenance, not a guess about the URL: 'betslip' where the feed
 * put the link on the outcome — the selection is pre-added when it opens —
 * 'event' where it put it on the market, the bookmaker or (racing) the race.
 * The button's words come off this, so a "slip" is only ever a slip the feed
 * said was one.
 */
export function legLink(match, selection, bookmakerKey) {
  const row = (match?.prices?.[selection] ?? []).find((r) => r.bookmaker === bookmakerKey);
  const link = bookLink(bookmakerKey, row?.link);
  return link ? { link, linkKind: row?.linkKind === 'betslip' ? 'betslip' : 'event' } : {};
}

/**
 * Which book a ticket is handed to, and whether the TICKET named it.
 *
 * A single carries `bookmakerKey` — it was priced at one book and that book is
 * the whole instruction. A multi does not: its legs sit at whatever the profile
 * prices at, or at three different books on a best-price ticket, and there is
 * no honest answer but "your book", which is the profile's default.
 *
 * `named` is the difference, and it is the difference the button's own words
 * make: "Open at Neds" says the price is Neds'; "Open Sportsbet" says only that
 * Sportsbet is where you bet.
 */
export function handoffBook(multi, defaultKey) {
  const named = bookmakerSite(multi?.bookmakerKey);
  if (named) return { ...named, named: true };
  const fallback = bookmakerSite(defaultKey);
  return fallback ? { ...fallback, named: false } : null;
}

/**
 * WHERE THE TAP ACTUALLY GOES: `{ url, slip, deep, legs }`, or null with no book.
 *
 * The leg's own link where the TICKET NAMED THE BOOK and the leg at that book
 * carried one; the book's front door otherwise. The three cases, in the words
 * the owner asked for them:
 *
 *   a single       one leg, one book, and the link is that leg's — a betslip
 *                  link opens the book with the selection already on the slip,
 *                  which is the "create a bet slip automatically" half.
 *   a multi at one book
 *                  the FIRST leg's link. No Australian book publishes a URL
 *                  that builds a multi, so the rest of the legs are the paste
 *                  they have always been — the slip goes to the clipboard with
 *                  the tap either way.
 *   anything else  the front door. A best-price multi and a Classic multi go to
 *                  the profile's own book, which is not the book that quoted any
 *                  particular leg: opening one leg's page there would be this
 *                  app choosing a leg the ticket never named.
 */
export function openTarget(book, legs) {
  if (!book) return null;
  const list = Array.isArray(legs) ? legs : [];
  const front = { url: book.url, slip: false, deep: false, legs: list.length };
  if (!book.named) return front;
  const leg = list.find((l) => l?.bookmakerKey === book.key && l?.link);
  const url = leg ? bookLink(book.key, leg.link) : null;
  if (!url) return front;
  return { url, slip: leg.linkKind === 'betslip', deep: true, legs: list.length };
}

/**
 * The button, and it says what the tap will actually do.
 *
 * "Open at Neds" on a ticket that names its book, "Open Sportsbet" otherwise —
 * and "Add to Neds slip" where the link waiting for it is a BETSLIP link, which
 * is a different act: the selection is on the slip when the app opens. Nothing
 * says "Add to slip" on a link the feed did not hand us as one.
 */
export const openLabel = (book, target = null) => {
  if (!book) return '';
  if (target?.slip) return `Add to ${book.name} slip`;
  return `${book.named ? 'Open at' : 'Open'} ${book.name}`;
};

/**
 * The toast the tap raises. It says where the slip went AND where to paste it,
 * because the app is about to disappear and this is the last thing on screen.
 *
 * On a betslip link there is nothing to paste for a single — the selection went
 * with the tap — and on a multi there is everything but the first leg, so the
 * line says which of the two happened rather than asking for a paste that is
 * already done.
 */
export const slipCopiedLine = (book, target = null) => {
  if (!book) return 'Slip copied';
  if (target?.slip) {
    return target.legs > 1
      ? `First leg sent to ${book.name} · the rest are on your clipboard`
      : `Selection sent to ${book.name} · slip copied too`;
  }
  return `Slip copied · paste it in ${book.name}`;
};

/** What the Place alert says instead of its usual line once the book has been opened. */
export const openedLine = (book) => (book ? `Opened ${book.name} · tap Place bet when it’s on.` : '');

// WHICH TICKET WENT TO WHICH BOOK, remembered for exactly as long as it is
// useful.
//
// A module variable rather than a field on the profile, because nothing here is
// worth persisting: it is a breadcrumb dropped by the row that opened the book
// and picked up seconds later by the Place alert, which on Today is three
// components away and on the ticket screen is the next control down. It is
// keyed by the ticket's own id so opening Neds for one ticket cannot put
// "Opened Neds" on the alert for another, and a reload forgets it, which is
// right — a punter who has restarted the app has not just come back from a
// bookmaker.
let opened = null;

/** Remember that `book` was opened for this ticket. */
export function rememberOpened(multiId, book) {
  opened = multiId && book ? { multiId: String(multiId), book } : null;
}

/** The book opened for this ticket, or null. */
export function lastOpened(multiId) {
  return opened && opened.multiId === String(multiId ?? '') ? opened.book : null;
}
